import { useState } from "react";
import { useProjektKennzahlen } from "../../hooks/usePlanung";
import { euro, type InterneKalkulation, type WebInvoiceDraft } from "../../lib/invoices";
import { computeWebDraftTotals, kalkSumme } from "../../lib/invoiceEditor";

type Props = {
  draft: WebInvoiceDraft;
  onChange: (draft: WebInvoiceDraft) => void;
  /** Im Reiter von InvoiceWerkzeuge: ohne eigene Karte und Überschrift. */
  eingebettet?: boolean;
};

const LEER: InterneKalkulation = {
  materialkosten: 0, lohn_helfer: 0, lohn_inhaber: 0, fahrt_sonstiges: 0,
};

const FELDER: { schluessel: keyof InterneKalkulation; label: string }[] = [
  { schluessel: "materialkosten", label: "Material" },
  { schluessel: "lohn_helfer", label: "Lohn Helfer" },
  { schluessel: "lohn_inhaber", label: "Lohn Inhaber" },
  { schluessel: "fahrt_sonstiges", label: "Fahrt & Sonstiges" },
];

/**
 * Interne Kalkulation — was der Beleg kostet, bevor er etwas einbringt.
 *
 * Bewusst grob: vier Zahlen reichen, um die Marge zu sehen. Sie steht
 * ausschließlich im Entwurf und wird von der PDF-Engine nicht ausgewertet —
 * der Kunde bekommt sie nie zu Gesicht. Vorbelegen lässt sie sich aus den
 * Projektkennzahlen, wenn am Beleg ein Projekt hängt.
 */
export default function InvoiceKalkulation({ draft, onChange, eingebettet = false }: Props) {
  const [hinweis, setHinweis] = useState("");
  const kennzahlen = useProjektKennzahlen(draft.source_project_id || undefined);
  const kalk = draft.interne_kalk || null;
  const totals = computeWebDraftTotals(draft);
  const kosten = kalkSumme(kalk);
  const deckung = Math.round((totals.net - kosten) * 100) / 100;
  const marge = totals.net > 0 ? Math.round((deckung / totals.net) * 1000) / 10 : 0;

  const setze = (schluessel: keyof InterneKalkulation, wert: number) => {
    onChange({ ...draft, interne_kalk: { ...(kalk || LEER), [schluessel]: wert } });
  };

  const ausProjekt = () => {
    if (!draft.source_project_id) {
      setHinweis("Ohne Projekt am Beleg gibt es nichts zu übernehmen — Projekt oben zuordnen.");
      return;
    }
    const kz = kennzahlen.data;
    if (!kz) {
      setHinweis("Die Kennzahlen des Projekts sind noch nicht geladen.");
      return;
    }
    onChange({
      ...draft,
      interne_kalk: {
        ...(kalk || LEER),
        materialkosten: Math.round(((kz.material_fuer_gewinn || 0) + (kz.verpflegung_fuer_gewinn || 0)) * 100) / 100,
        lohn_helfer: Math.round((kz.personalkosten_helfer || 0) * 100) / 100,
      },
    });
    setHinweis("Material und Helferlohn aus dem Projekt übernommen.");
  };

  const Huelle = eingebettet ? "div" : "section";
  return (
    <Huelle className={eingebettet ? "invoice-werkzeug-inhalt invoice-kalk-card" : "invoice-editor-card invoice-kalk-card"}>
      <div className="invoice-editor-card-head">
        {eingebettet ? (
          <span aria-hidden="true" />
        ) : (
          <div>
            <p className="invoice-kicker">Nur intern</p>
            <h2>Kalkulation</h2>
          </div>
        )}
        <button className="invoice-button" type="button" onClick={ausProjekt} disabled={kennzahlen.isFetching}>
          {kennzahlen.isFetching ? "Lädt …" : "⤓ Aus Projekt übernehmen"}
        </button>
      </div>
      <p className="invoice-editor-hint">
        Steht in keinem PDF und in keiner E-Rechnung — sie dient nur dazu, die Marge zu sehen,
        bevor der Beleg rausgeht.
      </p>
      {hinweis && <p className="invoice-inline-note" role="status">{hinweis}</p>}
      <div className="invoice-kalk-gitter">
        {FELDER.map(({ schluessel, label }) => (
          <label className="invoice-editor-field compact" key={schluessel}>
            <span>{label}</span>
            <div className="invoice-money-input">
              <input
                type="number"
                step="0.01"
                value={kalk ? kalk[schluessel] : 0}
                onChange={(event) => setze(schluessel, Number(event.target.value) || 0)}
              />
              <b>€</b>
            </div>
          </label>
        ))}
      </div>
      <div className="invoice-kalk-ergebnis">
        <span><small>Kosten</small><strong>{euro(kosten)}</strong></span>
        <span><small>Erlös netto</small><strong>{euro(totals.net)}</strong></span>
        <span className={deckung < 0 ? "negativ" : "positiv"}>
          <small>Deckungsbeitrag</small>
          <strong>{euro(deckung)}{kosten > 0 && totals.net > 0 ? ` · ${marge.toLocaleString("de-DE")} %` : ""}</strong>
        </span>
      </div>
    </Huelle>
  );
}
